/**
 * What Home is about (TMMAPP-0159 §C; Viewing is removed, §A). One fixed rule, written once
 * (`homeScopeOf`):
 *
 * - **A GUEST** (no books of their own) sees the virtual account shared with them: the first one by
 *   `/me` order. A guest with none sees their own (empty) Home.
 * - **Everyone else** sees their own money.
 *
 * **Shared with you** (`sharedSpaces`) is one card per virtual account shared with the person, or
 * shared by them with someone (`sharedWithNames` non-empty, TMMAPI-0186 U3): own first, then the
 * ones shared with them, each in `/me` order.
 */
import type { Me, MeVirtualAccount } from '@/api/me';
import { orderVirtualAccounts } from '@/shell/navigation';

/** Who else is in a space, as far as the data says. */
export type SpaceAudience =
  /** Someone else's virtual account, shared with you. */
  | { kind: 'sharedWithYou'; owner: string | null }
  /** One of yours that is shared, with the people it is shared with (`sharedWithNames`). */
  | { kind: 'sharedWith'; names: string[] };

export interface HomeSpace {
  virtualAccount: MeVirtualAccount;
  audience: SpaceAudience;
}

export type HomeScope = { kind: 'me' } | ({ kind: 'space' } & HomeSpace);

function spaceOf(virtualAccount: MeVirtualAccount): HomeSpace | null {
  if (!virtualAccount.mine) {
    return {
      virtualAccount,
      audience: {
        kind: 'sharedWithYou',
        owner: virtualAccount.ownerDisplayName ?? virtualAccount.sharedBy,
      },
    };
  }
  const names = virtualAccount.sharedWithNames;
  return names.length > 0 ? { virtualAccount, audience: { kind: 'sharedWith', names } } : null;
}

/** The Shared with you row: every shared virtual account, own first, then the ones shared with you. */
export function sharedSpaces(virtualAccounts: readonly MeVirtualAccount[]): HomeSpace[] {
  return orderVirtualAccounts([...virtualAccounts]).flatMap((virtualAccount) => {
    const space = spaceOf(virtualAccount);
    return space === null ? [] : [space];
  });
}

/** Home's scope: a GUEST's first shared virtual account by `/me` order, else the person's own. */
export function homeScopeOf(me: Pick<Me, 'tier' | 'virtualAccounts'>): HomeScope {
  if (me.tier !== 'GUEST') return { kind: 'me' };
  const first = me.virtualAccounts.find((virtualAccount) => !virtualAccount.mine);
  const space = first === undefined ? null : spaceOf(first);
  return space === null ? { kind: 'me' } : { kind: 'space', ...space };
}

/** The key Home's reads are cached under. */
export function homeScopeKey(scope: HomeScope): string {
  return scope.kind === 'me' ? 'me' : `va:${scope.virtualAccount.id}`;
}
